import React from 'react';
import glamorous from 'glamorous';

import FieldComponent from './field-component';
import { MENU_ENTRIES } from '../constants';

const FieldRecall = () => (
  <FieldComponent
    title="Field [recall]"
    description="Embedding field for all other fields"
    presence={["MANDATORY"]}
    subsection={MENU_ENTRIES.fieldsDescriptionRecall}
    preExample={(
      <div>
        The [lang] attribute of [recall] field is MANDATORY.<br/>
        It describes the language in which the recall details are provided (ISO 639-1 language code)<br/>
        The possible values are [en] for recalls in English and [fr] for recalls in French.<br/>
      </div>
    )}
    example={`<recall lang="en" xmlns="urn:recall-schema">
  ...
  [All other fields]
  ...
</recall>`}
  />
);

const FieldEditType = () => (
  <FieldComponent
    title="Field [edit_type]"
    description="Mode of import"
    presence={["MANDATORY"]}
    subsection={MENU_ENTRIES.fieldsDescriptionEditType}
    preExample={(
      <div>
        Possible values: [I] for insertion of a new recall, [U] for update of an existing recall, [D] for deletion of
        an existing recall.
      </div>
    )}
    example={`<recall lang="en" xmlns="urn:recall-schema">
  <edit_type>I</edit_type>
  ...
</recall>`}
  />
);

const FieldId = () => (
  <FieldComponent
    title="Field [id]"
    description="Unique identifier of the recall (defined by the jurisdiction/country provider)."
    presence={["MANDATORY"]}
    subsection={MENU_ENTRIES.fieldsDescriptionId}
    example={`<recall lang="en" xmlns="urn:recall-schema">
  <id>147373-ER-32535</id>
  ...
</recall>`}
  />
);

const CountryCodeExample = (
  <div>
    <p>
      List of available codes: <a href="https://www.iso.org/obp/ui/#iso:pub:PUB500001:en"
                                  target="_blank">https://www.iso.org/obp/ui/#iso:pub:PUB500001:en</a>
    </p>
    In addition, the following codes are accepted:
    <glamorous.Ul className="pt-list" css={{ listStyle: 'disc' }}>
      <li>AE-AZ for Abū Zaby [Abu Dhabi]</li>
      <li>AE-AJ for Ajmān</li>
      <li>AE-FU for Al Fujayrah</li>
      <li>AE-SH for Ash Shāriqah [Charjah]</li>
      <li>AE-DU for Dubayy [Dubaï]</li>
      <li>AE-RK for Ra’s al Khaymah</li>
      <li>AE-UQ for Umm al Qaywayn</li>
    </glamorous.Ul>
  </div>
);

const FieldCountryId = () => (
  <FieldComponent
    title="Field [country_id]"
    description="ISO3166 code for the country creating the recall. "
    presence={["MANDATORY"]}
    subsection={MENU_ENTRIES.fieldsDescriptionCountryId}
    preExample={CountryCodeExample}
    example={`<recall lang="en" xmlns="urn:recall-schema">
  <country_id>KR</country_id>
  ...
</recall>`}
  />
);

const FieldManufacturer = () => (
  <FieldComponent
    title="Field [manufacturer]"
    description="Name of the manufacturer of the product recalled"
    presence={["OPTIONAL"]}
    subsection={MENU_ENTRIES.fieldsDescriptionManufacturer}
    example={`<recall lang="en" xmlns="urn:recall-schema">
  <manufacturer>Toyz for the World Inc.</manufacturer >
  ...
</recall>`}
  />
);

const FieldManufacturerCountryId = () => (
  <FieldComponent
    title="Field [manufacturer_country_id]"
    description="ISO3166 code for the country who manufactured the recalled product."
    presence={["OPTIONAL", "REPEATABLE"]}
    subsection={MENU_ENTRIES.fieldsDescriptionManufacturerCountryId}
    preExample={CountryCodeExample}
    example={`<recall lang="en" xmlns="urn:recall-schema">
  <manufacturer_country_id>KR</manufacturer_country_id>
  <manufacturer_country_id>AZ</manufacturer_country_id>
  ...
</recall>`}
  />
);

const FieldProductType = () => (
  <FieldComponent
    title="Field [product_type]"
    description="Type or categorisation of the recalled product."
    presence={["OPTIONAL"]}
    subsection={MENU_ENTRIES.fieldsDescriptionProductType}
    preExample={(
      <glamorous.Div css={{ fontWeight: 'bold', marginTop: 10, '& code': { fontWeight: 'normal' } }} >
        Best practice:
        <glamorous.Ul className="pt-list" css={{ listStyle: 'disc' }}>
          <li>Do not insert HTML tags, just strings.</li>
          <li>
            If there are several product types, separate them clearly:
            <ul>
              <li>Not OK: <code>{`<product_type>Children’s Products/Toys</product_type>`}</code></li>
              <li>OK: <code>{`<product_type>Children’s Products ; Toys</product_type>`}</code></li>
            </ul>
          </li>
          <li>
            Preparing the string in the correct format will enhance search quality.
          </li>
        </glamorous.Ul>
      </glamorous.Div>
    )}
    example={`<recall lang="en" xmlns="urn:recall-schema">
  <product_type>Children’s Products ; Toys</product_type>
  ...
</recall>`}
  />
);

const FieldProductName = () => (
  <FieldComponent
    title="Field [product_name]"
    description="Name of the recalled product."
    presence={["MANDATORY (if import mode is different from “Deletion”) "]}
    subsection={MENU_ENTRIES.fieldsDescriptionProductName}
    preExample={(
      <glamorous.Div css={{ fontWeight: 'bold', marginTop: 10, '& code': { fontWeight: 'normal' } }}>
        Best practice:
        <glamorous.Ul className="pt-list" css={{ listStyle: 'disc' }}>
          <li>Do not insert HTML tags, just strings.</li>
          <li>
            The product name should be as precise as possible.
            <ul>
              <li>Not OK: <code>{`<b>Toy Magic</b>>`}</code></li>
              <li>OK: <code>{`Toy Magic`}</code></li>
            </ul>
          </li>
        </glamorous.Ul>
      </glamorous.Div>
    )}
    example={`<recall lang="en" xmlns="urn:recall-schema">
  <product_name>Model Car ByzXYZ234</product_name>
  ...
</recall>`}
  />
);

const FieldProductDesc = () => (
  <FieldComponent
    title="Field [product_desc]"
    description="Description of the recalled product."
    presence={["OPTIONAL"]}
    subsection={MENU_ENTRIES.fieldsDescriptionProductDesc}
    preExample={(
      <glamorous.Div css={{ fontWeight: 'bold', marginTop: 10, '& code': { fontWeight: 'normal' } }}>
        Best practice:
        <glamorous.Ul className="pt-list" css={{ listStyle: 'disc' }}>
          <li>Use this field to describe the product</li>
          <li>This is not the place for serial numbers. For serial numbers, use the field [product_model_no].</li>
        </glamorous.Ul>
      </glamorous.Div>
    )}
    example={`<!-- Example without HTML-encoding -->
<recall lang="en" xmlns="urn:recall-schema">
  <product_desc>Flat LED torch with fridge magnet and wrist strap. 'City of Gold Coast' is written on the front.</product_desc>
  ...
</recall>
<!-- Example with HTML-encoding -->
<recall lang="en" xmlns="urn:recall-schema">
  <product_desc>&lt;div&gt; Flat LED torch with fridge magnet and wrist strap.& lt;br/&gt;& lt;i&gt;City of Gold Coast&lt;/i&gt; is written on the front.& lt;/div&gt;</product_desc>
  ...
</recall>`}
  />
);

const FieldProductCode = ({ onSectionChange }) => (
  <FieldComponent
    title="Field [product_code]"
    description="GS1 Global Product Classification (GPC) codes categorising the recalled product."
    presence={["OPTIONAL"]}
    subsection={MENU_ENTRIES.fieldsDescriptionProductCode}
    preExample={(
      <div>
        Complex field: see section <a onClick={e => onSectionChange(MENU_ENTRIES.productCodesXmlExamples)}>Product
        codes</a>
      </div>
    )}
  />
);

const FieldProductModelNo = () => (
  <FieldComponent
    title="Field [product_model_no]"
    description="Originating country’s product model number and other information"
    presence={["OPTIONAL"]}
    subsection={MENU_ENTRIES.fieldsDescriptionProductModelNo}
    preExample={(
      <glamorous.Div css={{ fontWeight: 'bold', marginTop: 10, '& code': { fontWeight: 'normal' } }}>
        Best practice:
        <glamorous.Ul className="pt-list" css={{ listStyle: 'disc' }}>
          <li>
            Use this field to enter model numbers, serial numbers, serial number ranges, production dates etc.
          </li>
        </glamorous.Ul>
      </glamorous.Div>
    )}
    example={`<!-- Example without HTML-encoding -->
<recall lang="en" xmlns="urn:recall-schema">
  <product_model_no> B363R871-5002</product_model_no>
  ...
</recall>
<!-- Example with HTML-encoding -->
<recall lang="en" xmlns="urn:recall-schema">
  <product_model_no>B363R871-5002&lt;br/&gt;B363R871-5003&lt;br/&gt;B363R871-5004&lt;br/&gt;B363R871-5005</product_model_no>
  ...
</recall>`}
  />
);

const FieldHazard = () => (
  <FieldComponent
    title="Field [hazard]"
    description="Health hazard presented by the recalled product."
    presence={["OPTIONAL"]}
    subsection={MENU_ENTRIES.fieldsDescriptionHazard}
    example={`<!-- Example without HTML-encoding -->
<recall lang="en" xmlns="urn:recall-schema">
  <hazard>The user's finger may become caught in the stroller hinge while unfolding it, resulting in crushing and/or laceration injuries.</hazard>
  ...
</recall>
<!-- Example with HTML-encoding -->
<recall lang="en" xmlns="urn:recall-schema">
  <hazard>&lt;div&gt;The user's finger may become caught in the stroller hinge while unfolding it, resulting in :&lt;ul&gt;&lt;li&gt;crushing&lt;/li&gt;&lt;li&gt;or laceration injuries.&lt;/li&gt;&lt;/ul&gt;&lt;/div&gt;</hazard>
  ...
</recall>`}
  />
);

const FieldInjuries = () => (
  <FieldComponent
    title="Field [injuries]"
    description="Originating country’s injury information"
    presence={["OPTIONAL"]}
    subsection={MENU_ENTRIES.fieldsDescriptionInjuries}
    example={`<!-- Example without HTML-encoding -->
<recall lang="en" xmlns="urn:recall-schema">
  <injuries>The firm has received a total of 33 reports of incidents, of which 10 occurred after the October 2014 recall announcement.  The reports include pinched fingers, bruising and injuries to the head and face, some required stitches.</injuries>
  ...
</recall>
<!-- Example with HTML-encoding -->
<recall lang="en" xmlns="urn:recall-schema">
  <injuries> &lt;div&gt;The firm has received a total of 33 reports of incidents, of which 10 occurred after the October 2014 recall announcement. &lt;br/&gt;The reports include pinched fingers, bruising and injuries to the head and face, some required stitches.&lt;/div&gt;</injuries>
  ...
</recall>`}
  />
);

const FieldAction = () => (
  <FieldComponent
    title="Field [action]"
    description="Action consumers should take in regards to the recall OR action taken by the company undertaking the recall"
    presence={["OPTIONAL"]}
    subsection={MENU_ENTRIES.fieldsDescriptionAction}
    example={`<!-- Example without HTML-encoding -->
<recall lang="en" xmlns="urn:recall-schema">
  <action>Consumers should continue to use the stroller with the usual care but should contact phil&amp;teds via the web site at www.philandteds.com/support or by calling 1800-888586 to obtain a self-fit hinge cover kit. Returning the stroller to phil&amp;teds is not necessary. All retailers within our distribution network will also have kits available.</action>
  ...
</recall>
<!-- Example with HTML-encoding -->
<recall lang="en" xmlns="urn:recall-schema">
  <action>&lt;div&gt;Consumers should continue to use the stroller with the usual care but should contact phil&amp;teds via the &lt;a href=&quot;www.philandteds.com/support&quot;&gt;web site&lt;/a&gt; or by calling 1800-888586 to obtain a self-fit hinge cover kit.&lt;br/&gt;Returning the stroller to phil&amp;teds is not necessary.&lt;br/&gt;All retailers within our distribution network will also have kits available.&lt;/div&gt;</action>
  ...
</recall>`}
  />
);

const FieldUnits = () => (
  <FieldComponent
    title="Field [units]"
    description="Originating country’s number of units recalled and unit measurements"
    presence={["OPTIONAL"]}
    subsection={MENU_ENTRIES.fieldsDescriptionUnits}
    example={`<recall lang="en" xmlns="urn:recall-schema">
  <units>About 277,000 (in addition, 11,000 were sold in Canada)</units>
  ...
</recall>`}
  />
);

const FieldURL = () => (
  <FieldComponent
    title="Field [URL]"
    description="URL to originating country’s recall notice"
    presence={["OPTIONAL"]}
    subsection={MENU_ENTRIES.fieldsDescriptionURL}
    example={`<recall lang="en" xmlns="urn:recall-schema">
  <URL>https://www.productsafety.gov.au/recalls/content/index.phtml/itemId/1008977</URL>
  ...
</recall>`}
  />
);

const FieldDistributorName = ({ onSectionChange }) => (
  <FieldComponent
    title="Field [distributor_name]"
    description=""
    presence={["OPTIONAL"]}
    subsection={MENU_ENTRIES.fieldsDescriptionDistributorName}
    example={`<recall lang="en" xmlns="urn:recall-schema">
  <distributor_name>ABC Distribution</distributor_name>
  ...
</recall>`}
  />
);

const FieldDistributorWebsite = ({ onSectionChange }) => (
  <FieldComponent
    title="Field [distributor_website]"
    description=""
    presence={["OPTIONAL"]}
    subsection={MENU_ENTRIES.fieldsDescriptionDistributorWebsite}
    example={`<recall lang="en" xmlns="urn:recall-schema">
  <distributor_website>https://abc-distribution.com</distributor_website>
  ...
</recall>`}
  />
);

const FieldDate = () => (
  <FieldComponent
    title="Field [date]"
    description={(
      <div>
        This is the date the recall was announced in the originating country. Use ISO8601 for the date format.
        It <strong>MUST</strong> be a date only and not include a time component.
      </div>
    )}
    presence={["MANDATORY (if import mode is different from “Deletion”) "]}
    subsection={MENU_ENTRIES.fieldsDescriptionDate}
    example={`<recall lang="en" xmlns="urn:recall-schema">
  <date>2011-12-13</date>
  ...
</recall>`}
  />
);

const FieldGTIN = () => (
  <FieldComponent
    title="Field [GTIN]"
    description={(
      <div>
        Global Trade Item Number (GTIN) is a unique identifier for a trade item. GS1 defines trade items as products or
        services that are priced, ordered or invoiced at any point in the supply chain. For more information, see: <a
        href="https://www.gs1.org/standards/id-keys/gtin" target="_blank">https://www.gs1.org/standards/id-keys/gtin</a>
      </div>
    )}
    presence={["OPTIONAL"]}
    subsection={MENU_ENTRIES.fieldsDescriptionGTIN}
    preExample={(
      <glamorous.Div css={{ fontWeight: 'bold', marginTop: 10, '& code': { fontWeight: 'normal' } }}>
        Best practice:
        <glamorous.Ul className="pt-list" css={{ listStyle: 'disc' }}>
          <li>
            GTIN is the ideal way to uniquely identify a product. Best practice would be to always try and provide a
            GTIN (when available).
          </li>
        </glamorous.Ul>
      </glamorous.Div>
    )}
    example={`<recall lang="en" xmlns="urn:recall-schema">
  <GTIN>9781234567891</GTIN>
  ...
</recall>`}
  />
);

const FieldGTINProductType = () => (
  <FieldComponent
    title="Field [GTIN_product_type]"
    description="GTIN product type"
    presence={["OPTIONAL"]}
    subsection={MENU_ENTRIES.fieldsDescriptionGTINProductType}
    example={`<recall lang="en" xmlns="urn:recall-schema">
  <GTIN_product_type>Book</GTIN_product_type>
  ...
</recall>`}
  />
);

const FieldGTINDesc = () => (
  <FieldComponent
    title="Field [GTIN_desc]"
    description="GTIN description"
    presence={["OPTIONAL"]}
    subsection={MENU_ENTRIES.fieldsDescriptionGTINDesc}
    example={`<recall lang="en" xmlns="urn:recall-schema">
  <GTIN_desc>Description for the GTIN</GTIN_desc>
  ...
</recall>`}
  />
);

const FieldHTS = () => (
  <FieldComponent
    title="Field [HTS]"
    description="HTS code"
    presence={["OPTIONAL"]}
    subsection={MENU_ENTRIES.fieldsDescriptionHTS}
    example={`<recall lang="en" xmlns="urn:recall-schema">
  <HTS>123</HTS>
  ...
</recall>`}
  />
);

const FieldHTSProductType = () => (
  <FieldComponent
    title="Field [HTS_product_type]"
    description="HTS product type"
    presence={["OPTIONAL"]}
    subsection={MENU_ENTRIES.fieldsDescriptionHTSProductType}
    example={`<recall lang="en" xmlns="urn:recall-schema">
<HTS_product_type>HTS product type</HTS_product_type>
...
</recall>`}
  />
);

const FieldHTSDesc = () => (
  <FieldComponent
    title="Field [HTS_desc]"
    description="HTS description"
    presence={["OPTIONAL"]}
    subsection={MENU_ENTRIES.fieldsDescriptionHTSDesc}
    example={`<recall lang="en" xmlns="urn:recall-schema">
  <HTS_desc>HTS description</HTS_desc>
  ...
</recall>`}
  />
);

const FieldImage = ({ onSectionChange }) => (
  <FieldComponent
    title="Field [image]"
    description="Description of the image characteristics associated with the recall."
    presence={["OPTIONAL", "REPEATABLE"]}
    subsection={MENU_ENTRIES.fieldsDescriptionImage}
    preExample={(
      <div>
        Complex field: see section <a onClick={e => onSectionChange(MENU_ENTRIES.complexFieldImages)}>Images</a>
      </div>
    )}
  />
);

const FieldManufacturerWebsite = () => (
  <FieldComponent
    title="Field [manufacturer_website]"
    description="URL of the manufacturer website"
    presence={["OPTIONAL"]}
    subsection={MENU_ENTRIES.fieldsDescriptionManufacturerWebsite}
    preExample={(
      <glamorous.Div css={{ fontWeight: 'bold', marginTop: 10, '& code': { fontWeight: 'normal' } }}>
        Best practice:
        <glamorous.Ul className="pt-list" css={{ listStyle: 'disc' }}>
          <li>Enter just one (and only one) URL</li>
          <li>Enter only the URL: no descriptive text</li>
        </glamorous.Ul>
      </glamorous.Div>
    )}
    example={`<recall lang="en" xmlns="urn:recall-schema">
  <manufacturer_website>http://www.adidas.com</manufacturer_website>
  ...
</recall>`}
  />
);

const FieldSeriousRisk = () => (
  <FieldComponent
    title="Field [serious_risk]"
    description="Identifies whether the risk is serious or not"
    presence={["OPTIONAL"]}
    subsection={MENU_ENTRIES.fieldsDescriptionSeriousRisk}
    preExample={(
      <glamorous.Div css={{ fontWeight: 'bold', marginTop: 10, '& code': { fontWeight: 'normal' } }}>
        Best practice:
        <glamorous.Ul className="pt-list" css={{ listStyle: 'disc' }}>
          <li>
            Valid values: true, false
          </li>
        </glamorous.Ul>
      </glamorous.Div>
    )}
    example={`<recall lang="en" xmlns="urn:recall-schema">
  <serious_risk>true</serious_risk>
  ...
</recall>
`}
  />
);

export default ({ onSectionChange }) => (
  <div>
    <FieldRecall/><br/>
    <FieldEditType/><br/>
    <FieldId/><br/>
    <FieldCountryId/><br/>
    <FieldManufacturer/><br/>
    <FieldManufacturerCountryId/><br/>
    <FieldProductType/><br/>
    <FieldProductName/><br/>
    <FieldProductDesc/><br/>
    <FieldProductCode onSectionChange={onSectionChange}/><br/>
    <FieldProductModelNo/><br/>
    <FieldHazard/><br/>
    <FieldInjuries/><br/>
    <FieldAction/><br/>
    <FieldUnits/><br/>
    <FieldURL/><br/>
    <FieldDate/><br/>
    <FieldDistributorName/><br/>
    <FieldDistributorWebsite/><br/>
    <FieldGTIN/><br/>
    <FieldGTINProductType/><br/>
    <FieldGTINDesc/><br/>
    <FieldHTS/><br/>
    <FieldHTSProductType/><br/>
    <FieldHTSDesc/><br/>
    <FieldImage onSectionChange={onSectionChange}/><br/>
    <FieldManufacturerWebsite/><br/>
    <FieldSeriousRisk/><br/>
  </div>
);


// WEBPACK FOOTER //
// ./src/views/static/import-documentation/field-description/fields.js